import moment, { Moment } from "moment"
import _ from "lodash"
import { IDate, EDayOfWeek, ISimpleTime, ActiveTimeButton, TimeBarViewType, IDateRange, ITimezone } from "../models"
import * as utils from "./utils"
import * as hierarchyHelper from "./hierarchyHelper"
import AccountStore from "../stores/AccountStore"
import * as api from "../api"
import jstz from "jstz"

export function isUTC(dateString: string): boolean {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
        throw new Error("Invalid date string");
    }
    if (dateString.endsWith('Z')) {
        return true
    }
    return date.toISOString() === dateString
}

export function keepConvertUTCToLocal(dateTime: IDate) {
    const utcTime = moment(dateTime).tz("UTC")
    return moment()
        .year(utcTime.year())
        .month(utcTime.month())
        .date(utcTime.date())
        .hour(utcTime.hour())
        .minute(utcTime.minute())
        .second(utcTime.second())
        .millisecond(utcTime.millisecond())
        .format()
}

export function getHourMinuteAmFromSimpleTime(simpleTime: ISimpleTime) {
    const hour = (simpleTime.Hour === 12 || simpleTime.Hour === 0) ? "12" : (simpleTime.Hour % 12) < 10 ? "0" + String(simpleTime.Hour % 12) : String(simpleTime.Hour % 12)
    const minute = simpleTime.Minute < 10 ? "0" + String(simpleTime.Minute) : String(simpleTime.Minute)
    const ampm = simpleTime.Hour < 12 ? "am" : "pm"
    return { hour, minute, ampm }
}

export function getTimeStringFormatFromSimpleTime(simpleTime: ISimpleTime) {
    const time = getHourMinuteAmFromSimpleTime(simpleTime)
    return `${time.hour} : ${time.minute} ${time.ampm}`
}

export function getTimeStringSimpleFormatFromSimpleTime(simpleTime: ISimpleTime) {
    const hour = (simpleTime.Hour === 12 || simpleTime.Hour === 0) ?
        12 :
        String(simpleTime.Hour % 12)
    const minute = simpleTime.Minute < 10 ? "0" + String(simpleTime.Minute) : String(simpleTime.Minute)
    const ampm = simpleTime.Hour < 12 ? "am" : "pm"
    return `${hour}:${minute}${ampm}`
}

export function getStartOfWeek(startDayOfWeek: EDayOfWeek, referenceDate: Moment | Date | string): Moment {
    const date = _.cloneDeep(referenceDate)
    const weekday = moment(date).day()
    const adjust = weekday === EDayOfWeek.Sunday ?
        startDayOfWeek === EDayOfWeek.Sunday ?
            1 :
            0 :
        startDayOfWeek > weekday ?
            -1 :
            0
    return moment(date).startOf("isoWeek").isoWeekday(startDayOfWeek).add(adjust, "week")
}

export function getEndOfWeek(startDayOfWeek: EDayOfWeek, referenceDate: Moment | Date | string): Moment {
    const date = _.cloneDeep(referenceDate)
    return getStartOfWeek(startDayOfWeek, date).add(6, "days").endOf("day")
}

export function getPassedDateTime(time: Date | string): string {
    const diffStr = moment(time).fromNow()
    return diffStr.includes('month') || diffStr.includes("year") ? moment(time).format("MM/DD/YYYY") : diffStr
}

export function getSimpleTimeFromHourMinuteAm(hour: string, minute: string, ampm: string): ISimpleTime {
    let intHour = parseInt(hour, 10)
    if (intHour !== 12 && ampm === "pm") intHour += 12
    if (intHour === 12 && ampm === "am") intHour = 0
    const intMinute = parseInt(minute, 10)
    return { Hour: intHour, Minute: intMinute }
}

export function convertToHour(minutes) {
    return utils.roundToTwo(minutes / 60)
}

export function convertToMinute(hours) {
    return utils.roundToTwo(hours * 60)
}

export function convertToHourAndMinute(val) {
    const hours = Math.floor(val / 60)
    const minutes = Math.round(val % 60)
    return { hours, minutes }
}

export function getWeekRange(startDayOfWeek: EDayOfWeek, referenceDate: IDate) {
    const startDate = getStartOfWeek(startDayOfWeek, referenceDate).format()
    const endDate = getEndOfWeek(startDayOfWeek, referenceDate).format()
    return { startDate, endDate }
}

export const getActiveButton = (startDayOfWeek: EDayOfWeek, selectedDate: IDate, view: TimeBarViewType): ActiveTimeButton => {
    let res: ActiveTimeButton = null
    let start
    let end
    if (view === "week") {
        const thisWeek = getWeekRange(startDayOfWeek, moment().format());
        start = thisWeek.startDate
        end = thisWeek.endDate
    }
    else {
        start = moment().startOf(view)
        end = moment().endOf(view)
    }

    const date = moment(selectedDate)
    res = date.isSameOrAfter(moment(start).subtract(1, view)) && date.isSameOrBefore(moment(end).subtract(1, view)) ?
        "last" :
        date.isSameOrAfter(moment(start)) && date.isSameOrBefore(moment(end)) ?
            "this" :
            date.isSameOrAfter(moment(start).add(1, view)) && date.isSameOrBefore(moment(end).add(1, view)) ?
                "next" : null
    return res
}

export const getShortTime = (_date: Date): string => {
    return _date.getMinutes() === 0
        ? moment(_date)
            .format("ha")
            .replace("m", "")
        : moment(_date)
            .format("h:mma")
            .replace("m", "")
}

export function getDateRangeListStr(dateRangeList: IDateRange[]): string {
    if (dateRangeList.length === 0)
        return ""
    dateRangeList.sort((a, b) => moment(a.StartDate).isBefore(b.StartDate) ? -1 : 0)
    return dateRangeList.map(i => `${moment(i.StartDate).format("MM/DD/YYYY")}-${moment(i.EndDate).format("MM/DD/YYYY")}`).join(", ")
}

export function convertDefaultDateToNull(dateTime: IDate) {
    // 0001 is the default Year set by BackEnd
    if ((dateTime as string).includes("0001")) {
        return null
    }
    return dateTime
}

export function convertMinutesToHourStr(minutes: number): string {
    return minutes % 60 === 0 ?
        `${Math.floor(minutes / 60)}h` :
        `${Math.floor(minutes / 60)}h ${Math.round((minutes % 60) * 100) / 100}m`
}

export async function getIANATimezoneNameFromHierarchyJobId(_hierarchyItemId: string, _jobId: string): Promise<string> {
    if (AccountStore.getJobTimezoneEnabled() && _jobId) {
        const storedJobTimezoneId = AccountStore.getJobTimezoneId(_jobId)
        if (storedJobTimezoneId === undefined) {
            const res = await api.getJobIANATimezoneId(_jobId)
            AccountStore.setJobTimezoneId(_jobId, res.data)
            if (res.data) {
                return res.data
            }
        }
        else {
            if (storedJobTimezoneId)
                return storedJobTimezoneId
        }
    }

    const hierarchyItem = hierarchyHelper.getHierarchyItemById(_hierarchyItemId)
    const accountSettings = AccountStore.getCurrentAccountSettings()
    return hierarchyItem && hierarchyItem.Location
        ? hierarchyItem.Location.IANATimezoneId
        : accountSettings && accountSettings.LocalizationSettings
            ? accountSettings.LocalizationSettings.DefaultIANATimezoneId
            : jstz.determine().name()
}

export function getTimezoneTranslatedDateTime(dateString: IDate, minute: number, hour: number, timezoneId: string) {
    //dateString should be browser local time
    const year = moment(dateString).year()
    const month = moment(dateString).month()
    const date = moment(dateString).date()
    return getTimezoneTranslatedDateTimeFromLocal(year, month, date, minute, hour, timezoneId)
}

export function getTimezoneTranslatedDateTimeFromLocal(year: number, month: number, day: number, minute: number, hour: number, timezoneId: string) {
    let date = moment().tz(timezoneId)
    date = date
        .month(month)
        .date(day)
        .year(year)
        .hours(hour)
        .minute(minute)
        .seconds(0)

    return date.utc().format()
}

export async function getIANATimezoneFromHierarchyJobId(_hierarchyItemId: string, jobId: string): Promise<ITimezone> {
    const tzName = await getIANATimezoneNameFromHierarchyJobId(_hierarchyItemId, jobId)
    return AccountStore.getTimezones().find((_tz) => _tz.Name === tzName)
}

//this method will get a client timezone local time based on utc time
export async function getDateTime(_hierarchyItemId: string, _jobId: string, _dateTime: IDate): Promise<Moment> {
    return moment(_dateTime).tz(await getIANATimezoneNameFromHierarchyJobId(_hierarchyItemId, _jobId))
}

export function getDayOfWeekArrayByDateRange(startDate: IDate, endDate: IDate): EDayOfWeek[] {
    const weekdays: EDayOfWeek[] = []
    for (let date = moment(startDate); date.isSameOrBefore(endDate); date.add(1, 'day')) {
        const dayOfWeek = date.weekday()
        weekdays.findIndex(i => i === dayOfWeek) === -1 &&
            weekdays.push(dayOfWeek)
    }
    return weekdays
}